import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Pagination from '../../common/Pagination';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';
import useSocietyMosques from '../../../hooks/useSocietyMosques';
import { getMosqueUrl } from '../../../utils/urlHelpers';
import { getListingCoverImageUrl, IMAGE_PLACEHOLDER_PATH } from '../../../utils/imageFallback';

const SocietyMosques = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    sort: 'newest'
  });

  const { mosques, loading, error, totalCount, totalPages } = useSocietyMosques({
    societyId: society?.id,
    currentPage,
    search: filters.search,
    sort: filters.sort,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [society?.id]);

  useEffect(() => {
    if (!error || !onError) return;
    console.error('Society mosques load error:', error);
    onError('Failed to load mosques');
  }, [error, onError]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Safety check
  if (!society) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center text-gray-500">Loading society information...</div>
      </div>
    );
  }

  const activeFilterCount =
    (filters.sort !== 'newest' ? 1 : 0) + (filters.search ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Mosques"
        count={totalCount}
        itemLabel={{ singular: 'mosque', plural: 'mosques' }}
        societyName={society.name}
        searchPlaceholder="Search mosques..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Add Mosque',
                onClick: () =>
                  navigate(`/societies/${society.slug}/mosques/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="name">Name A-Z</option>
          <option value="capacity">Capacity</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Mosques Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : mosques.length > 0 ? (
          mosques.map((mosque) => (
            <div 
              key={mosque.id} 
              className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
              onClick={() => navigate(getMosqueUrl(mosque))}
            >
              {/* Mosque Image */}
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <img
                  src={getListingCoverImageUrl(mosque)}
                  alt={mosque.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                  }}
                />
              </div>

              {/* Mosque Info */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
                  {mosque.name}
                </h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {mosque.address || 'No description available'}
                </p>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="line-clamp-1">{mosque.address}</span>
                  </div>
                  {mosque.imam_name && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>Imam: {mosque.imam_name}</span>
                    </div>
                  )}
                </div>

                {/* Contact Info */}
                {mosque.phone && (
                  <div className="mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{mosque.phone}</span>
                    </div>
                  </div>
                )}

                <div className="flex space-x-2">
                  <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200">
                    View Details
                  </button>
                  {mosque.phone && (
                    <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors duration-200">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-lg shadow-sm border p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No mosques yet</h3>
            <p className="text-gray-500 mb-4">Be the first to add a mosque to this society.</p>
            {isAuthenticated && (
              <button
                type="button"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                onClick={() =>
                  navigate(`/societies/${society.slug}/mosques/create?society=${society.id}`)
                }
              >
                Add First Mosque
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyMosques;
